export type WeekDay = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export interface TimeSlot {
  start: number;  // minutes from midnight
  end: number;    // minutes from midnight
}

export type BookingType = 'flash' | 'custom'
export type CustomDesignStatus = 'design_ready' | 'design_missing'
export type BookingStatus = 'scheduled' | 'completed' | 'no_show' | 'cancelled'

export interface BookingCompleteForm {
  completedAt: Date
  finalPrice?: number
  actualDate?: Date
  notes?: string
}

export interface Booking {
  id: string
  chatId: string
  userId: string
  artistId: string
  clientUsername: string
  type: BookingType
  shortSummary: string
  flashId?: string
  flashImageUrl?: string
  customImageUrl?: string
  customDesignStatus?: CustomDesignStatus
  scheduledTimestamp: Date
  createdAt: Date
  updatedAt?: Date
  estimatedTime?: number  // in minutes
  googleCalendarEventId?: string
  iosCalendarEventId?: string  // iOS calendar event ID
  status?: BookingStatus
  messageId?: string // Links booking to the specific availability message it was created from
  bookingCompleteForm?: BookingCompleteForm
}

export interface DaySettings {
  isEnabled: boolean;
  timeSlots: TimeSlot[];
}

export interface WeeklySchedule {
  monday: DaySettings;
  tuesday: DaySettings;
  wednesday: DaySettings;
  thursday: DaySettings;
  friday: DaySettings;
  saturday: DaySettings;
  sunday: DaySettings;
}

export interface IosCalendarOverride {
  eventId: string       // From iOS calendar
  eventTitle: string    
  startDate: number     // timestamp in milliseconds
  endDate: number       // timestamp in milliseconds
  isBlocked: boolean    // True = blocks availability, False = optional
}

export interface ArtistAvailability {
  mode: 'month' | 'week';  // Determines if using month or week view
  weekSchedule: WeeklySchedule;  // Renamed from weeklySchedule
  dateOverrides: {
    [date: string]: DaySettings;
  };
  monthSchedule?: {
    [date: string]: DaySettings;  // For month view availability
  };
  googleOverrides?: {
    [date: string]: DaySettings;
  };
  bookingOverrides?: {
    [date: string]: DaySettings;  // Existing bookings that block availability
  };
  iosOverrides?: IosCalendarOverride[];  // iOS calendar events as availability blockers
}

// Booking Required Information types
export type BookingFieldType = 'text' | 'number' | 'consentForm'

export interface TextFieldConfig {
  type: 'text'
  displayText: string
  displayName?: string // Fallback for backward compatibility
  hint?: string
  isRequired: boolean
  placeholder?: string
}

export interface NumberFieldConfig {
  type: 'number'
  displayText: string
  displayName?: string // Fallback for backward compatibility
  hint?: string
  isRequired: boolean
  placeholder?: string
}

export interface ConsentFormFieldConfig {
  type: 'consentForm'
  displayText: string
  displayName?: string // Fallback for backward compatibility
  isRequired: boolean
  content: string // The consent text to display in the popup
}

export type BookingRequiredInformationField = TextFieldConfig | NumberFieldConfig | ConsentFormFieldConfig

export interface BookingRequiredInformation {
  [fieldName: string]: BookingRequiredInformationField
}

// Booking Link types for secure direct booking
export interface BookingLink {
  linkId: string
  artistId: string
  estimatedTime: number | 'unknown'
  earliestDate: Date
  createdAt: Date
  isActive: boolean
  expiresAt: Date | null
  metadata: Record<string, any>
}

// Default schedule template
export const DEFAULT_WEEKLY_SCHEDULE: WeeklySchedule = {
  monday: {
    isEnabled: true,
    timeSlots: [{ start: 540, end: 1020 }]  // 9:00 - 17:00
  },
  tuesday: {
    isEnabled: true,
    timeSlots: [{ start: 540, end: 1020 }]
  },
  wednesday: {
    isEnabled: true,
    timeSlots: [{ start: 540, end: 1020 }]
  },
  thursday: {
    isEnabled: true,
    timeSlots: [{ start: 540, end: 1020 }]
  },
  friday: {
    isEnabled: true,
    timeSlots: [{ start: 540, end: 1020 }]
  },
  saturday: {
    isEnabled: false,
    timeSlots: []
  },
  sunday: {
    isEnabled: false,
    timeSlots: []
  }
};
